import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Star, Phone, Edit2, Trash2, X, Check, RefreshCw } from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import { getMechanics, createMechanic, updateMechanic, deleteMechanic } from '../api';
import './MechanicsPage.css';

const STATUS_META = {
  ACTIVE:   { label: 'Active',   cls: 'badge-success' },
  LEAVE:    { label: 'On Leave', cls: 'badge-warning' },
  TRAINING: { label: 'Training', cls: 'badge-info'    },
  INACTIVE: { label: 'Inactive', cls: 'badge-neutral' },
};

const EMPTY_FORM = { name: '', role: '', phone: '', experienceYears: '', specialties: '', status: 'ACTIVE' };

export default function MechanicsPage() {
  const [mechanics,  setMechanics]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editItem,   setEditItem]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMechanics();
      setMechanics(data || []);
    } catch (e) {
      console.error('Failed to load mechanics:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditItem(m);
    setForm({
      name:            m.name || '',
      role:            m.role || '',
      phone:           m.phone || '',
      experienceYears: m.experienceYears || '',
      specialties:     (m.specialties || []).join(', '),
      status:          m.status || 'ACTIVE',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim()) return setError('Name is required.');
    if (!form.role.trim()) return setError('Role is required.');

    const payload = {
      name:            form.name.trim().toUpperCase(),
      role:            form.role.trim().toUpperCase(),
      phone:           form.phone.trim(),
      experienceYears: parseInt(form.experienceYears) || 0,
      specialties:     form.specialties.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
      status:          form.status,
    };

    setSaving(true);
    try {
      if (editItem) {
        const updated = await updateMechanic(editItem.id, payload);
        setMechanics(prev => prev.map(m => m.id === editItem.id ? updated : m));
      } else {
        const created = await createMechanic(payload);
        setMechanics(prev => [...prev, created]);
      }
      setShowModal(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to save mechanic.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this mechanic?')) return;
    try {
      await deleteMechanic(id);
      setMechanics(prev => prev.filter(m => m.id !== id));
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.message || e.message));
    }
  };

  const getInitials = (name) =>
    (name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <TopBar title="Mechanics" subtitle="Team management and assignments" />
      <div className="admin-content">
        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Mechanics Team</h1>
            <p>{mechanics.filter(m => m.status === 'ACTIVE').length} active of {mechanics.length} total</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> Add Mechanic</button>
            <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'spinning' : ''} />
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="mech-summary stagger">
          {[
            { label: 'Active',   count: mechanics.filter(m => m.status === 'ACTIVE').length,   color: 'var(--success)' },
            { label: 'On Leave', count: mechanics.filter(m => m.status === 'LEAVE').length,    color: 'var(--warning)' },
            { label: 'Training', count: mechanics.filter(m => m.status === 'TRAINING').length, color: 'var(--info)'    },
            { label: 'Total',    count: mechanics.length,                                       color: 'var(--accent)'  },
          ].map(({ label, count, color }) => (
            <div key={label} className="mech-summary-card card afu">
              <span className="mech-summary-card__count" style={{ color }}>{count}</span>
              <span className="mech-summary-card__label">{label}</span>
            </div>
          ))}
        </div>

        {/* Loading */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            <div className="appt-spinner" />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading mechanics…</p>
          </div>
        ) : mechanics.length === 0 ? (
          <div className="appt-empty">
            <p>No mechanics yet. Click "Add Mechanic" to get started.</p>
          </div>
        ) : (
          <div className="mech-grid stagger">
            {mechanics.map(m => {
              const sm = STATUS_META[m.status] || STATUS_META.ACTIVE;
              const initials = m.initials || getInitials(m.name);
              return (
                <div key={m.id} className="mech-card card afu">
                  <div className="mech-card__top">
                    <div className="mech-card__photo-wrap">
                      {m.photoUrl ? (
                        <>
                          <img src={m.photoUrl} alt={m.name} className="mech-card__photo"
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          <div className="mech-card__avatar" style={{ display: 'none' }}>{initials}</div>
                        </>
                      ) : (
                        <div className="mech-card__avatar">{initials}</div>
                      )}
                    </div>
                    <div className="mech-card__info">
                      <h3 className="mech-card__name">{m.name}</h3>
                      <p className="mech-card__role">{m.role}</p>
                      <span className={`badge ${sm.cls}`}>{sm.label}</span>
                    </div>
                    <div className="mech-card__actions">
                      <button className="tbl-btn" onClick={() => openEdit(m)} title="Edit"><Edit2 size={13} /></button>
                      <button className="tbl-btn tbl-btn--danger" onClick={() => handleDelete(m.id)} title="Remove"><Trash2 size={13} /></button>
                    </div>
                  </div>

                  <div className="mech-card__stats">
                    <div className="mech-stat">
                      <span className="mech-stat__val">{m.rating ? m.rating.toFixed(1) : '—'}</span>
                      <span className="mech-stat__lbl"><Star size={10} fill="var(--accent)" color="var(--accent)" /> Rating</span>
                    </div>
                    <div className="mech-stat">
                      <span className="mech-stat__val">{m.totalJobs || 0}</span>
                      <span className="mech-stat__lbl">Jobs Done</span>
                    </div>
                    <div className="mech-stat">
                      <span className="mech-stat__val">{m.experienceYears || 0}yr</span>
                      <span className="mech-stat__lbl">Experience</span>
                    </div>
                  </div>

                  {m.phone && (
                    <div className="mech-card__contact">
                      <Phone size={12} /><span>{m.phone}</span>
                    </div>
                  )}

                  <div className="mech-card__specs">
                    {(m.specialties || []).map(s => <span key={s} className="mech-spec-tag">{s}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal__header">
                <h3 className="modal__title">{editItem ? 'Edit Mechanic' : 'Add New Mechanic'}</h3>
                <button className="modal__close" onClick={() => setShowModal(false)}><X size={18} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { label: 'Full Name *',                  field: 'name',            placeholder: 'e.g. RAJESH KUMAR' },
                  { label: 'Role / Title *',               field: 'role',            placeholder: 'e.g. SENIOR MECHANIC' },
                  { label: 'Phone Number',                 field: 'phone',           placeholder: '+91 XXXXX XXXXX' },
                  { label: 'Years of Experience',          field: 'experienceYears', placeholder: 'e.g. 8', type: 'number' },
                  { label: 'Specialties (comma-separated)', field: 'specialties',    placeholder: 'ENGINE, AC SERVICE, BRAKES' },
                ].map(({ label, field, placeholder, type }) => (
                  <div key={field}>
                    <label className="label">{label}</label>
                    <input className="input" type={type || 'text'} placeholder={placeholder}
                      style={{ textTransform: type ? 'none' : 'uppercase' }}
                      value={form[field]}
                      onChange={e => setForm(f => ({ ...f, [field]: type ? e.target.value : e.target.value.toUpperCase() }))} />
                  </div>
                ))}
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="ACTIVE">Active</option>
                    <option value="LEAVE">On Leave</option>
                    <option value="TRAINING">Training</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              </div>
              {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 10 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : <><Check size={14} /> {editItem ? 'Save Changes' : 'Add Mechanic'}</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}